import React, { useEffect, useMemo, useState } from 'react';

import { Popover } from '@mui/material';
import { noop } from 'lodash';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { IAssigneeOptionsList, useAssigneeOptionsList } from 'hooks/useAssigneeOptionsList';

import {
    FixedDateMenuOptions,
    TSideOption,
    TabOptions,
    TodayMenuOptions,
    VersionDateMenuOptions,
    defaultValuesByTabOption,
} from 'components/common/CustomFilters/DateRangeFilter/DateRangeFilter.utils';
import { Header } from 'components/common/CustomFilters/DateRangeFilter/Hedear/Header';
import { MainContent } from 'components/common/CustomFilters/DateRangeFilter/MainContent/MainContent';
import { SideMenu } from 'components/common/CustomFilters/DateRangeFilter/SideMenu/SideMenu';
import { OptionsWithAssignee } from 'components/common/MyCustomSelect/components/OptionsWithAssignee/OptionsWithAssignee';

import { IFields } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/MainFilters.types';
import { Select } from 'components/DesignSystem/Select';
import { TextInput } from 'components/DesignSystem/TextInput/TextInput';

import classes from './hasCommentsFilter.module.scss';

const getDefaultActiveSideMenuOption = (value): string => {
    if (Array.isArray(value)) {
        return 'FIXED_DATE';
    }

    if (value) {
        return value;
    } else {
        return 'ANYTIME';
    }
};

interface Data {
    value?: string | string[] | undefined;
    type?: TabOptions | undefined;
    commenter: IAssigneeOptionsList | null;
    mentioned: IAssigneeOptionsList | null;
}

interface Props {
    onChange: (data: Data) => void;
    value: Data;
    inputDefaultValue: string;
    fixedDateMenuOptions: { value: string; label: string; dateValue: number[] }[];
    todayMenuOptions: { value: string; label: string; inputLabel: string }[];
    versionDateMenuOptions: { value: string; label: string; inputLabel: string }[];
    watch: UseFormWatch<IFields>;
}

export const HasCommentsFilter = ({
    onChange,
    value,
    inputDefaultValue = '',
    fixedDateMenuOptions = FixedDateMenuOptions,
    todayMenuOptions = TodayMenuOptions,
    versionDateMenuOptions = VersionDateMenuOptions,
    watch,
}: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [menuOpen, setMenuOpen] = useState<boolean>(false);

    const [inputLabel, setInputLabel] = useState(inputDefaultValue);

    const [activeTab, setActiveTab] = useState<TabOptions>(value.type ?? 'TODAY_REF');
    const [activeSideMenuOption, setActiveSideMenuOption] = useState<string>(
        getDefaultActiveSideMenuOption(value.value),
    );
    const [rangeDate, setRangeDate] = useState(
        Array.isArray(value.value) && value.value.length === 2 ? value.value.map(Number) : [],
    );

    const [commenter, setCommenter] = useState<IAssigneeOptionsList | null>(null);
    const [mentioned, setMentioned] = useState<IAssigneeOptionsList | null>(null);

    useEffect(() => {
        setInputLabel(inputDefaultValue);
    }, [inputDefaultValue]);

    useEffect(() => {
        if (value.type !== activeTab) {
            setActiveSideMenuOption(defaultValuesByTabOption[activeTab]);
        } else {
            setActiveSideMenuOption(getDefaultActiveSideMenuOption(value.value));
        }
    }, [activeTab]);

    useEffect(() => {
        value.type && setActiveTab(value.type);
        setActiveSideMenuOption(getDefaultActiveSideMenuOption(value.value));
        setCommenter(value.commenter);
        setMentioned(value.mentioned);
    }, [value]);

    const sideMenuOptions = useMemo((): TSideOption<string>[] => {
        switch (activeTab) {
            case 'FIXED_DATE':
                return fixedDateMenuOptions;
            case 'TODAY_REF':
                return todayMenuOptions;
            case 'VERSION_DATE_REF':
                return versionDateMenuOptions;
            default:
                return fixedDateMenuOptions;
        }
    }, [activeTab, fixedDateMenuOptions, todayMenuOptions, versionDateMenuOptions]);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
        setMenuOpen(true);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setMenuOpen(false);
        setRangeDate([]);
        setCommenter(value.commenter);
        setMentioned(value.mentioned);
    };

    const handleApply = (value, label) => {
        const commenterLabel = commenter ? ` Commenter: ${watch('commenter')?.label}` : undefined;
        const mentionedLabel = mentioned ? ` Mentioned: ${watch('mentioned')?.label}` : undefined;
        const filterLabel = [`${label}`, commenterLabel, mentionedLabel].filter(Boolean).join();
        setInputLabel(filterLabel);
        onChange({
            value: value,
            type: activeTab,
            commenter: commenter,
            mentioned: mentioned,
        });

        handlePopoverClose();
    };

    const handleClear = () => {
        setInputLabel('');
        setActiveTab('TODAY_REF');
        onChange({
            value: undefined,
            type: undefined,
            commenter: null,
            mentioned: null,
        });
    };

    const { options } = useAssigneeOptionsList(false);

    return (
        <div className={classes.hasCommentsFilterContainer}>
            <TextInput
                id={'battlecards-filters-has-comments'}
                value={inputLabel}
                onClear={handleClear}
                onClick={handleMenuOpen}
                autoComplete={'off'}
                className={classes.inputWidth}
                onChange={noop}
            />

            <Popover
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                disableScrollLock
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                open={menuOpen}
            >
                <div className={classes.popoverContentWrapper}>
                    <div className={classes.selectContainer}>
                        <div className={classes.mainTitle}>Find Comments</div>
                        <div className={classes.selectWrapper}>
                            <span className={classes.title}>Commenter</span>
                            <Select<IAssigneeOptionsList>
                                value={commenter}
                                options={options}
                                isClearable={true}
                                components={{
                                    Option: OptionsWithAssignee,
                                }}
                                onChange={(value) => setCommenter(value)}
                            />
                        </div>
                        <div className={classes.selectWrapper}>
                            <span className={classes.title}>Mentioned</span>
                            <Select<IAssigneeOptionsList>
                                value={mentioned}
                                options={options}
                                isClearable={true}
                                components={{
                                    Option: OptionsWithAssignee,
                                }}
                                onChange={(value) => setMentioned(value)}
                            />
                        </div>
                    </div>
                    <div>
                        <Header activeTab={activeTab} setActiveTab={setActiveTab} />
                        <div className={classes.dateRangeWrapper}>
                            <SideMenu
                                menuOptions={sideMenuOptions}
                                activeSideMenuOption={activeSideMenuOption}
                                setActiveSideMenuOption={setActiveSideMenuOption}
                            />
                            <MainContent
                                rangeDate={rangeDate}
                                setRangeDate={setRangeDate}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                onApply={handleApply}
                                activeSideMenuOption={activeSideMenuOption}
                                setActiveSideMenuOption={setActiveSideMenuOption}
                                menuOptions={sideMenuOptions}
                                existingData={value}
                                handlePopoverClose={handlePopoverClose}
                            />
                        </div>
                    </div>
                </div>
            </Popover>
        </div>
    );
};
